import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import {
  FaBriefcase,
  FaCode,
  FaReact,
  FaTrophy,
  FaMedal,
  FaCodeBranch,
  FaExternalLinkAlt,
  FaJava,
  FaBookReader,
  FaChevronDown,
  FaChevronRight,
  FaBuilding,
  FaFire,
  FaRocket,
  FaUserTie,
  FaUserCog,
  FaUserGraduate,
} from "react-icons/fa";
import {
  SiTailwindcss,
  SiMongodb,
  SiSap,
  SiMysql,
  SiLeetcode,
  //SiCodeforces,
  SiNodedotjs,
  SiMui,
  SiCplusplus,
  // SiSpring,
  // SiSpringboot,
  SiC,
  SiLinux,
  SiPython,
  SiJavascript,
  SiCss3,
  SiHtml5,
  // SiPandas,
  SiCodingninjas,
  SiMedium,
  SiDevdotto,
  SiGit,
  SiGithub,
  SiNextdotjs,
  SiTypescript,
  SiRedis,
} from "react-icons/si";

const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds
const DEV_TO_CACHE_KEY = "devto_articles_cache";
const MEDIUM_CACHE_KEY = "medium_articles_cache";

async function fetchDevToArticles() {
  // 1. Try to get from cache first
  try {
    const cachedData = localStorage.getItem(DEV_TO_CACHE_KEY);
    if (cachedData) {
      const { data, timestamp } = JSON.parse(cachedData);
      // Use cache if it's not expired and has data
      if (Date.now() - timestamp < CACHE_DURATION && data && data.length > 0) {
        console.log("Using cached Dev.to articles");
        return data;
      }
    }
  } catch (error) {
    console.warn("Cache retrieval failed:", error);
    // Continue to fetch fresh data
  }

  // 2. Fetch fresh data with proper error handling
  try {
    const response = await fetch(
      "https://dev.to/api/articles?username=codetirtho97",
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        // Add a timeout to prevent hanging requests
        signal: AbortSignal.timeout(10000), // 10 second timeout
      }
    );

    if (!response.ok) {
      throw new Error(
        `Dev.to API returned ${response.status}: ${response.statusText}`
      );
    }

    const data = await response.json();

    if (!Array.isArray(data)) {
      throw new Error("Dev.to API returned invalid data format");
    }

    console.log("Dev.to API returned", data.length, "articles");

    // UPDATED: Sort the articles by public reactions (most popular first), then by date
    data.sort((a, b) => {
      const reactionsA = a.public_reactions_count || 0;
      const reactionsB = b.public_reactions_count || 0;

      // Primary sort by reactions (descending)
      if (reactionsB !== reactionsA) {
        return reactionsB - reactionsA;
      }

      // Secondary sort by date (newest first) if reactions are equal
      return new Date(b.published_at) - new Date(a.published_at);
    });

    // Process and transform the data, limiting to 7 articles
    const processedArticles = data.slice(0, 7).map((article) => ({
      id: article.id,
      title: article.title,
      cover: article.cover_image || "/images/default_cover.jpg",
      date: new Date(article.published_at).toDateString(),
      link: article.url,
      reactions: article.public_reactions_count || 0,
      tags: article.tag_list || [],
      // Add a formatted date field for easier sorting/display
      formattedDate: new Date(article.published_at).toLocaleString(),
      published_at: article.published_at,
      // Add reading time if available
      readingTime: article.reading_time_minutes || 0,
    }));

    // Log the processed articles for debugging
    console.log(
      "Processed Dev.to articles (sorted by reactions):",
      processedArticles.map((a) => ({
        title: a.title.substring(0, 30) + "...",
        reactions: a.reactions,
        date: a.formattedDate,
      }))
    );

    // Save to cache
    localStorage.setItem(
      DEV_TO_CACHE_KEY,
      JSON.stringify({
        data: processedArticles,
        timestamp: Date.now(),
      })
    );

    return processedArticles;
  } catch (error) {
    console.error("Dev.to fetch failed:", error);

    // 3. Fall back to cached data even if expired
    try {
      const cachedData = localStorage.getItem(DEV_TO_CACHE_KEY);
      if (cachedData) {
        const { data } = JSON.parse(cachedData);
        if (data && data.length > 0) {
          console.log("Using expired Dev.to cache as fallback");
          return data;
        }
      }
    } catch (e) {
      console.error("Cache fallback failed:", e);
    }

    // 4. Last resort - return empty array (no hardcoded data)
    console.warn("No Dev.to articles available - using empty array");
    return [];
  }
}

async function fetchMediumArticles() {
  // 1. Try to get from cache first
  try {
    const cachedData = localStorage.getItem(MEDIUM_CACHE_KEY);
    if (cachedData) {
      const { data, timestamp } = JSON.parse(cachedData);
      // Use cache if it's not expired and has data
      if (Date.now() - timestamp < CACHE_DURATION && data && data.length > 0) {
        console.log("Using cached Medium articles");
        return data;
      }
    }
  } catch (error) {
    console.warn("Medium cache retrieval failed:", error);
  }

  // 2. Fetch fresh data with proper error handling
  try {
    const response = await fetch(
      "https://api.rss2json.com/v1/api.json?rss_url=https://medium.com/feed/@codetirtho97",
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
        signal: AbortSignal.timeout(10000), // 10 second timeout
      }
    );

    if (!response.ok) {
      throw new Error(
        `Medium API returned ${response.status}: ${response.statusText}`
      );
    }

    const mediumData = await response.json();

    if (mediumData.status !== "ok" || !Array.isArray(mediumData.items)) {
      throw new Error("Medium API returned invalid data format");
    }

    // Process and transform the data
    const processedArticles = mediumData.items.slice(0, 30).map((article) => ({
      id: article.guid,
      title: article.title,
      // Extract published date for better formatting options
      publishedDate: new Date(article.pubDate),
      date: new Date(article.pubDate).toDateString(),
      link: article.link,
      // Add any thumbnail if available (some RSS feeds include them)
      thumbnail: article.thumbnail || null,
      // Extract author info if available
      author: article.author || "Tirthoraj Bhattacharya",
      // Extract categories/tags if available
      categories: Array.isArray(article.categories) ? article.categories : [],
    }));

    // Save to cache
    localStorage.setItem(
      MEDIUM_CACHE_KEY,
      JSON.stringify({
        data: processedArticles,
        timestamp: Date.now(),
      })
    );

    return processedArticles;
  } catch (error) {
    console.error("Medium fetch failed:", error);

    // 3. Fall back to cached data even if expired
    try {
      const cachedData = localStorage.getItem(MEDIUM_CACHE_KEY);
      if (cachedData) {
        const { data } = JSON.parse(cachedData);
        if (data && data.length > 0) {
          console.log("Using expired Medium cache as fallback");
          return data;
        }
      }
    } catch (e) {
      console.error("Medium cache fallback failed:", e);
    }

    // 4. Last resort - return empty array
    console.warn("No Medium articles available - using empty array");
    return [];
  }
}

function Experience() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const urlTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(urlTab || "skills");
  const [activeSkillsTab, setActiveSkillsTab] = useState("Languages");
  const [expandedJob, setExpandedJob] = useState(null);
  //const [expandedCodingIndex, setExpandedCodingIndex] = useState([]);
  const [activePubTab, setActivePubTab] = useState("devto");
  const [devToArticles, setDevToArticles] = useState([]);
  const [mediumArticles, setMediumArticles] = useState([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlTab = searchParams.get("tab");
    if (urlTab && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [location.search, activeTab]);

  useEffect(() => {
    async function fetchArticles() {
      try {
        // Fetch both Dev.to and Medium articles in parallel
        const [devToArticles, mediumArticles] = await Promise.all([
          fetchDevToArticles(),
          fetchMediumArticles(),
        ]);

        setDevToArticles(devToArticles);
        setMediumArticles(mediumArticles);
      } catch (error) {
        console.error("Error fetching articles:", error);
      }
    }

    fetchArticles();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-fade-in");
        }
      },
      { threshold: 0.1 }
    );

    const currentSection = sectionRef.current;
    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  // function getPlatformUrl(platformName) {
  //   const username = "CodeTirtho97"; // Your username

  //   switch (platformName.toLowerCase()) {
  //     case "leetcode":
  //       return `https://leetcode.com/u/${username}`;
  //     case "code360": // Not sure about the exact URL format, adjust if needed
  //       return "https://www.naukri.com/code360/profile/CodeTirtho";
  //     case "codeforces":
  //       return `https://codeforces.com/profile/${username}`;
  //     default:
  //       return `https://www.google.com/search?q=${platformName}+${username}`; // Fallback
  //   }
  // }

  // Experience data
  const experience = {
    company: "TATA CONSULTANCY SERVICES",
    positions: [
      {
        title: "IT Analyst",
        duration: "2023 - 2024",
        description: [
          "Led SAP infrastructure upgrades across 3 client environments, streamlining deployment processes and reducing downtime by 25%.",
          "Built predictive maintenance tools that cut onboarding time by 30% and reduced critical system failures.",
          "Implemented automated monitoring solutions for enterprise systems, ensuring 99% uptime across production environments.",
        ],
      },
      {
        title: "System Engineer",
        duration: "2021 - 2023",
        description: [
          "Created SAP integration solutions with custom APIs, improving system upgrade processing time by 25%.",
          "Optimized database performance through custom tooling, improving SQL query execution by 20% and automating 50% of tasks.",
          "Orchestrated cross-functional SAP modifications, delivering seamless system updates with zero business disruption.",
        ],
      },
      {
        title: "Assistant System Engineer",
        duration: "2019 - 2021",
        description: [
          "Developed automated scripts for ticket processing and system monitoring, improving resolution efficiency by 20%.",
          "Enhanced SAP user authorization workflows through process optimization, reducing manual setup time by 30%.",
          "Managed 100+ daily client requests with 95% resolution rate, maintaining high-quality service standards.",
        ],
      },
    ],
  };

  const skills = {
    Languages: [
      {
        icon: SiCplusplus,
        name: "C++",
        level: "Intermediate",
        color: "#00a8e8",
      },
      { icon: SiC, name: "C", level: "Intermediate", color: "#b5179e" },
      { icon: FaJava, name: "Java", level: "Intermediate", color: "#0077b6" },
      {
        icon: SiPython,
        name: "Python",
        level: "Intermediate",
        color: "#b79ced",
      },
      { icon: SiHtml5, name: "HTML5", level: "Proficient", color: "#E34F26" },
      { icon: SiCss3, name: "CSS3", level: "Intermediate", color: "#1572B6" },
      {
        icon: SiJavascript,
        name: "JavaScript",
        level: "Intermediate",
        color: "#f9dc5c",
      },
      {
        icon: SiTypescript,
        name: "TypeScript",
        level: "Basic",
        color: "#79b8ff",
      },
    ],
    Frameworks: [
      {
        icon: FaReact,
        name: "React.js",
        level: "Intermediate",
        color: "#77b7d7",
      },
      { icon: SiNextdotjs, name: "Next.js", level: "Basic", color: "#d17724" },
      // { icon: SiSpring, name: "Spring", level: "Basic", color: "#80ea6e" },
      // {
      //   icon: SiSpringboot,
      //   name: "SpringBoot",
      //   level: "Basic",
      //   color: "#6db33f",
      // },
      {
        icon: SiNodedotjs,
        name: "Node.js",
        level: "Intermediate",
        color: "#84ba64",
      },
      {
        icon: SiTailwindcss,
        name: "Tailwind",
        level: "Basic",
        color: "#06B6D4",
      },
      { icon: SiMui, name: "MaterialUI", level: "Basic", color: "#006bd6" },
    ],
    Databases: [
      { icon: SiMongodb, name: "MongoDB", level: "Basic", color: "#40916c" },
      { icon: SiMysql, name: "MySQL", level: "Intermediate", color: "#48cae4" },
      { icon: SiRedis, name: "Redis", level: "Basic", color: "#FF4438" },
    ],
    Tools: [
      { icon: SiSap, name: "SAP", level: "Intermediate", color: "#0070f2" },
      { icon: SiGit, name: "Git", level: "Intermediate", color: "#f14e32" },
      {
        icon: SiGithub,
        name: "GitHub",
        level: "Intermediate",
        color: "#adb5bd",
      },
      {
        icon: FaCode,
        name: "VSCode",
        level: "Intermediate",
        color: "#0078d4",
      },
      // { icon: SiPandas, name: "Pandas", level: "Basic", color: "#9d4edd" },
      { icon: SiLinux, name: "Linux/Unix", level: "Basic", color: "#ff6c0e" },
    ],
  };

  // Custom CodeForces Icon Component
  const CodeForcesIcon = ({ size = 24, style = {} }) => (
    <img
      src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAxklEQVR4nO2VQQqDMBBFXZlrKHgFh+6KyZWEXqDpDcx11GuIhyhOFrqaEkttY7NpmYWLfHgwhGEehCFJkhiOZAZvWTMtuUFyuDprUPMJmvfwTWKmmU2Q74a/OIaAWlFQn9bUpZcVV7ei4BP06Ui9oB0Do0BQiMMJ7udTYSuorSwvKxXU7oxNYBWMVgF5yHLgFFCIKNgSr4j+36JOLIE1nX99rlHCsh+OEma3ptqTPOurL0D99eEY9HpQlfpT4mpU4PXEJKE8ANaBm3KEvYSKAAAAAElFTkSuQmCC"
      alt="CodeForces"
      width={size}
      height={size}
      style={style}
    />
  );

  // Custom HackerRank Icon Component
  const HackerRankIcon = ({ size = 24, style = {} }) => (
    <img
      src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsTAAALEwEAmpwYAAACn0lEQVR4nLWVTWgTQRTHA15EBQ+K3vQm3U13tjEoIeAHePIsTbr5oDqT5CAK3rwUj+pNUC9eFFFBkmyabk1MYzVmxorVtKbSakvTtBWp2otY61diG5ltvrM7WQ8uvON7v33v/+b/TCYDX2dC6jAPuJ5yYWdRJDAPMLpgTQV2GsllFx5Gu82D7jgfcqxzQUeJhkhQOeA3QNBl66PA9n8ufCTVu5lXpAgXdqxVCrcCNgIQtAwwPNsd7N5kqDj3wO3hI87V5sKcDqAKwigrEJ+NOQ5uQJriQk1FQ46SGHH/bgdQA8M1gOF1MOTZ2lDcnPDYObmnWqQSfMi5Hskm59/lpr8fVE6ttAVUxwbfWklgTw0QlWa1xmGJen+8mB5bzufzJSnZt2AUUIbcrAJ4WX/mypvheQroirh/VQEYfhKJLyBieJEBGK0JG+4p6gHkiaQKALKrUAHQEdA8IX1yP0P0hVoHGutYifBEIk8BnbKk/kT5DczQvK4R/wGG4J9rHdQ9pOa4Px6fKwP+1Gkwp3ZAfDZ9AFqpAYJOzeI07o4rOQo4kTz//vjDcx8BgTdEDC+pHaT9doYGRUOA2xlllgIqUb/ewjPfIX0NYKEeoDuiW2P9agf2Qf8XS7/np0hgBmA0RPNEgo4aGhFL5HvZWK5ZZEDgktoBhseMicxY03B5i4T6NcXoQztAw5pyDHO78vLOzJPJ50sNHWC4uPEO4GFDD03PKhhuepXmWUbgXoDRq7ZWIcR7bVpmxwCk+dTpbQLxWQFGXzVuxFSD2dEPyJ5d5qhrssWug9r3gNqFSNCqhl1fs2YCW0x6Hx9zuViaiPp7/5p5cDROpqy1vlrrKBJ0xvDJbBnb/zj6LaCYd59ZcT3mZGeBGp1IYF/HqHeHkeS/qeSzsbRFzg8AAAAASUVORK5CYII="
      alt="HackerRank"
      width={size}
      height={size}
      style={style}
    />
  );

  // Custom GeeksforGeeks Icon Component
  const GeeksforGeeksIcon = ({ size = 24, style = {} }) => (
    <img
      src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAAAsTAAALEwEAmpwYAAAD10lEQVR4nO1YTYgdRRDuaMSAiIKKQUUP4kUETVY9KDqZ6pmqdhVvuYgK/h38uyshPjwtnkIUhcW3U/U2/qCiB6MoKgYUQRFU8BD/iKDJxd8kmyUuxF2pyZvdntk3Oz1ZwSfMBwPvUdVV39fTXV09xnTo0KFDhw4dOvw7sEJL+sSS3gzPw4VW6HFgfBeE9lvGn0HoMAj+AkJfWqYMJL0l2hdtPNV8E9MTZ8SZm9RYVuirYezDw1z7NXfC+JhyUU4Fv0YBVvAoCP618n+tB7+xjK4teSUOjN+G5DjJBY+2EOA9THMgNBULTqSz6VnJdHIOCF5vmZ4BxgXPd1ev1zutifj2V7efrmOXyTEugNDTNnPXaew8xyC51go+BUzHRnEKFgCMXySD5NLaWRScAKFD3tt42SyZDXX+KhAYX1mZHDwIGW2tjd+fvEyX1ikJyF/vwJ5X61yMGbgtIDjvjdtR5wuMT3jLYj7l9Jqm+NGLt51vmb5vJ4BpcRunNzYF98Y96Qk4MWpWdVmA4N+egF5o/HwDMy0GC9Ddb1rghv7tZ5fXK75d9QGhd/x9pWvdtIAVei9YQJLhHW2C52MZXyotwRl3ZWFLZ9OrylWFXmgbP2a6K1hAtMdd0jYBCN3vk9T6vWxj3OHbYsF72se/9eJGAeuBVqRK7f6gsFmmD0uVZOC2mHEDnDy5vfMDf1i2MR3wbW7GXWDGDRFHm8plmI4VNr/M5gJ2uzPNuCHaF22sHv+FrXJiL62nf1qFsH5nfB/zvxdQlCgQfKtq1L5FN6cJwLBv8Wv9d4VNf/u2tvorHxFPbrZMr40g/qZyLjkn/eSiCvkT2h2aQFjBuytJdhU2ENxdFufuDI0bcXSucyntIZ7cPJoE09yyI+MfoUlykozvr4ylRb/WxzN4td/LaFvRJrYV+tOb2CNrOOKPnoBfg8lntLXUqDHtXeXjLVH1VVEmNL7Q717sA/WOTJ97jr+FXgst06fe7M/F4i6v+iVZckXlPPhEx4bk0NXgxf+s3lGwP6qOr3lBEZoprf0M763zTxgfKO8T7Ifc4vxzBISm6x0F49Jmy+imOl9t+LRtLhFi2tlIRrBXLha0d1VF8UUPXFSuYC5qSEBT3oDjOkvA7r5kQKSXd51hy7incgtbAKaHm8h7Ih4pzyrOW8HZYWynuTTn8O0e92Z/qjn6ktlgB+4h3e1hhwl+pJ2oaYn8hsb4cUgO/dSSCD7YKoF2jJbxUcv4hn4HAqGfdKZUGDB9DYzPWqZtZp0AwRiYnhvGPJLnyHNpTnxd39ZYdq8dOnTo0KFDhw7mv8c/hXdKvMBwaFwAAAAASUVORK5CYII="
      alt="GeeksforGeeks"
      width={size}
      height={size}
      style={style}
    />
  );

  // Custom InterviewBit Icon Component
  const InterviewBitIcon = ({ size = 32, style = {} }) => (
    <img
      src="data:image/png;base64,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"
      alt="InterviewBit"
      width={size}
      height={size}
      style={style}
    />
  );

  // Streamlined codingAchievements with best stats only
  const codingAchievements = [
    {
      platform: "LeetCode",
      icon: SiLeetcode,
      profileUrl: "https://leetcode.com/u/CodeTirtho97/",
      stats: [
        { label: "Problems Solved", value: "321", icon: FaCodeBranch },
        { label: "Contest Rating", value: "1,591", icon: FaTrophy },
        { label: "Top Percentile", value: "24.46%", icon: FaMedal },
      ],
      color: "#FFA116",
    },
    {
      platform: "Code360",
      icon: SiCodingninjas,
      profileUrl: "https://www.naukri.com/code360/profile/CodeTirtho",
      stats: [
        { label: "Problems Solved", value: "182", icon: FaCodeBranch },
        { label: "DSA Badges", value: "13", icon: FaMedal },
        { label: "Specialist Level", value: "5 Topics", icon: FaTrophy },
      ],
      color: "#f66c3b",
    },
    {
      platform: "GeeksforGeeks",
      icon: GeeksforGeeksIcon, // Using custom GFG icon
      profileUrl: "https://www.geeksforgeeks.org/user/tirthorajbhho6y/",
      stats: [
        { label: "Institute Rank", value: "1589", icon: FaTrophy },
        { label: "Coding Score", value: "278", icon: FaCodeBranch },
        { label: "Problems Solved", value: "83", icon: FaMedal },
      ],
      color: "#2F8D46",
    },
    {
      platform: "InterviewBit",
      icon: InterviewBitIcon,
      profileUrl: "https://www.interviewbit.com/profile/codetirtho97/",
      stats: [
        { label: "Global Rank", value: "118,370", icon: FaTrophy },
        { label: "Total Score", value: "5104", icon: FaCodeBranch },
        { label: "Problems Solved", value: "23", icon: FaMedal },
      ],
      color: "#1e40af",
    },
    {
      platform: "HackerRank",
      icon: HackerRankIcon, // Using custom HackerRank icon
      profileUrl: "https://www.hackerrank.com/profile/tirthorajbhatta1",
      stats: [
        { label: "Problem Solving", value: "⭐⭐⭐", icon: FaTrophy },
        { label: "Python Badge", value: "⭐⭐", icon: FaCodeBranch },
        { label: "Active Profile", value: "Verified", icon: FaMedal },
      ],
      color: "#00EA64",
    },
    {
      platform: "CodeForces",
      icon: CodeForcesIcon, // Using custom CodeForces icon
      profileUrl: "https://codeforces.com/profile/CodeTirtho97",
      stats: [
        { label: "Problems Solved", value: "63", icon: FaCodeBranch },
        { label: "Max Streak", value: "4 Days", icon: FaTrophy },
        { label: "Active Month", value: "62 Solved", icon: FaMedal },
      ],
      color: "#318CE7",
    },
  ];

  // Define the main tabs
  const tabs = [
    { id: "experience", label: "Professional Experience", icon: FaBriefcase },
    { id: "skills", label: "Technical Skills", icon: FaCode },
    { id: "coding", label: "Coding Profiles", icon: FaTrophy },
    { id: "publications", label: "Publications", icon: FaBookReader },
  ];

  // Define skills tabs
  const skillsTabs = ["Languages", "Frameworks", "Databases", "Tools"];

  return (
    <div className="min-h-screen bg-gray-900 py-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 opacity-95"></div>

      <div className="max-w-7xl mx-auto relative z-10" ref={sectionRef}>
        {/* Page Title */}
        <motion.h2
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl md:text-6xl font-bold text-center font-['Inter'] mb-12 mt-16 text-gray-100"
        >
          <span className="text-emerald-400 hover:cursor-default">
            Professional Journey & Skills
          </span>
          <p className="text-white text-lg font-bold mt-3 font-['Inter'] hover:cursor-default">
            Crafting solutions, mastering skills.
          </p>
        </motion.h2>

        {/* Removed "At-a-Glance" summary section */}

        {/* Enhanced Tabbed Navigation with glowing effects */}
        <div className="flex overflow-x-auto mb-8 pb-2 no-scrollbar">
          <div className="flex space-x-3 mx-auto relative">
            {tabs.map((tab) => (
              <motion.button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`flex items-center px-5 py-3 rounded-lg transition-all duration-300 whitespace-nowrap relative ${
                  activeTab === tab.id
                    ? "bg-emerald-400/20 text-emerald-400 border border-emerald-400 shadow-lg shadow-emerald-400/20"
                    : "bg-gray-800/40 text-gray-400 hover:bg-gray-700/40 hover:text-emerald-400 border border-gray-700"
                }`}
              >
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="tab-background"
                    className="absolute inset-0 bg-emerald-400/5 rounded-lg -z-10"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
                <tab.icon className="mr-2" />
                <span className="font-medium">{tab.label}</span>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {/* Professional Experience Tab */}
          {activeTab === "experience" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="bg-gray-800/25 rounded-xl shadow-lg p-6 border border-emerald-400/25 overflow-hidden"
            >
              <div className="flex items-center mb-8 cursor-default relative">
                <div className="absolute -top-6 -left-6 w-24 h-24 bg-emerald-400/10 rounded-full blur-2xl"></div>
                <div className="p-3 bg-emerald-400/10 rounded-lg mr-4 relative">
                  <FaBuilding className="text-emerald-400 text-2xl" />
                </div>
                <h3 className="text-xl md:text-2xl font-semibold text-emerald-400 tracking-wide">
                  {experience.company}
                </h3>
              </div>

              {/* Enhanced Interactive Timeline */}
              <div className="relative pl-10 pb-2">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-emerald-300 via-emerald-400 to-emerald-600 rounded-full shadow-lg shadow-emerald-400/20"></div>

                {experience.positions.map((position, index) => (
                  <div key={index} className="mb-12 last:mb-0">
                    {/* Timeline node with visual enhancement */}
                    <motion.div
                      whileHover={{ scale: 1.2 }}
                      className="absolute -left-2 w-5 h-5 bg-gradient-to-br from-emerald-300 to-emerald-600 rounded-full border-2 border-gray-900 cursor-pointer shadow-lg shadow-emerald-400/40 z-10"
                      onClick={() =>
                        setExpandedJob(expandedJob === index ? null : index)
                      }
                    />

                    {/* Position Year Label */}
                    <div className="absolute -left-32 top-0 w-24 text-right">
                      <div className="text-emerald-400 font-bold opacity-80">
                        {position.duration.split(" - ")[0]}
                      </div>
                    </div>

                    {/* Position Card */}
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.4,
                        delay: index * 0.1,
                        ease: "easeOut",
                      }}
                      className={`bg-gray-800/50 backdrop-blur-sm rounded-xl shadow-xl border ${
                        expandedJob === index
                          ? "border-emerald-400 bg-gradient-to-br from-gray-800/80 to-gray-900/80"
                          : "border-gray-700 hover:border-emerald-400/50 hover:shadow-emerald-400/5"
                      } transition-all duration-300 cursor-pointer overflow-hidden group`}
                      onClick={() =>
                        setExpandedJob(expandedJob === index ? null : index)
                      }
                    >
                      {/* Subtle gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                      {/* Job Header with improved styling */}
                      <div className="p-5 flex flex-col sm:flex-row sm:justify-between sm:items-center relative">
                        <div className="flex items-center mb-3 sm:mb-0">
                          <div className="p-2 bg-emerald-400/10 rounded-lg mr-4">
                            {index === 0 ? (
                              <FaUserTie className="text-emerald-400 text-xl flex-shrink-0" />
                            ) : index === 1 ? (
                              <FaUserCog className="text-emerald-400 text-xl flex-shrink-0" />
                            ) : (
                              <FaUserGraduate className="text-emerald-400 text-xl flex-shrink-0" />
                            )}
                          </div>
                          <div>
                            <h4 className="text-xl font-semibold text-gray-100 group-hover:text-emerald-400 transition-colors">
                              {position.title}
                            </h4>
                            <p className="text-gray-400 text-sm">
                              {position.duration}
                            </p>
                          </div>
                        </div>
                        <motion.div
                          animate={{ rotate: expandedJob === index ? 180 : 0 }}
                          transition={{
                            duration: 0.4,
                            ease: [0.4, 0.0, 0.2, 1],
                          }}
                          className="w-8 h-8 rounded-full bg-emerald-400/10 flex items-center justify-center text-emerald-400"
                        >
                          <FaChevronDown />
                        </motion.div>
                      </div>

                      {/* Job Details with smoother animation */}
                      <AnimatePresence>
                        {expandedJob === index && (
                          <motion.div
                            key={`job-details-${index}`}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{
                              height: "auto",
                              opacity: 1,
                              transition: {
                                height: {
                                  duration: 0.4,
                                  ease: [0.4, 0.0, 0.2, 1],
                                },
                                opacity: { duration: 0.3, delay: 0.1 },
                              },
                            }}
                            exit={{
                              height: 0,
                              opacity: 0,
                              transition: {
                                height: {
                                  duration: 0.3,
                                  ease: [0.4, 0.0, 0.2, 1],
                                },
                                opacity: { duration: 0.2 },
                              },
                            }}
                            className="overflow-hidden"
                          >
                            <div className="px-5 pb-5">
                              <div className="pl-4 border-l-2 border-emerald-400/30 space-y-4 pt-2">
                                {position.description.map((item, itemIndex) => (
                                  <motion.div
                                    key={itemIndex}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{
                                      duration: 0.3,
                                      delay: 0.1 + itemIndex * 0.1,
                                    }}
                                    className="flex group/item"
                                  >
                                    <div className="mt-1 mr-3 flex-shrink-0 w-5 h-5 rounded-full bg-emerald-400/10 flex items-center justify-center">
                                      <FaChevronRight className="text-emerald-400 text-xs group-hover/item:text-emerald-300" />
                                    </div>
                                    <p className="text-gray-300 text-sm leading-relaxed group-hover/item:text-white transition-colors">
                                      {item}
                                    </p>
                                  </motion.div>
                                ))}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Technical Skills Tab */}
          {activeTab === "skills" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="bg-gray-800/25 rounded-xl shadow-lg p-6 border border-emerald-400/25"
            >
              {/* Skills Categories - Redesigned to be more distinctive and less like nested tabs */}
              <div className="mb-10 relative">
                <div className="flex items-center justify-center">
                  <div className="inline-flex bg-gray-800/50 p-1.5 rounded-full shadow-lg border border-gray-700">
                    {skillsTabs.map((tab) => (
                      <motion.button
                        key={tab}
                        onClick={() => setActiveSkillsTab(tab)}
                        className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 relative overflow-hidden ${
                          activeSkillsTab === tab
                            ? "text-gray-900"
                            : "text-gray-400 hover:text-white"
                        }`}
                      >
                        {activeSkillsTab === tab && (
                          <motion.div
                            layoutId="skills-pill-bg"
                            className="absolute inset-0 bg-emerald-400"
                            style={{ borderRadius: 9999 }}
                            transition={{
                              type: "spring",
                              stiffness: 500,
                              damping: 30,
                            }}
                          />
                        )}
                        <span className="relative z-10">{tab}</span>
                      </motion.button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Skills Grid with revised styling to match pill selector */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
                {skills[activeSkillsTab].map((skill, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="bg-gray-800/30 rounded-2xl p-6 border border-gray-700/50 hover:border-emerald-400/30 transition-all duration-300 flex flex-col items-center group shadow-lg hover:shadow-emerald-400/5"
                  >
                    <div className="p-3 bg-gray-900/50 rounded-full mb-4 group-hover:bg-emerald-400/10 transition-colors duration-300">
                      <skill.icon
                        className="w-10 h-10 transition-transform duration-300 group-hover:scale-110"
                        style={{ color: skill.color }}
                      />
                    </div>
                    <p className="text-gray-200 font-medium text-center mb-3">
                      {skill.name}
                    </p>

                    {/* Skill Level Indicator - Fixed for proper dot display */}
                    <div className="mt-auto flex items-center">
                      <div className="flex space-x-1">
                        <div
                          className={`w-3 h-3 rounded-full ${
                            skill.level === "Basic" ||
                            skill.level === "Intermediate" ||
                            skill.level === "Proficient"
                              ? "bg-emerald-400"
                              : "bg-gray-600"
                          }`}
                        ></div>
                        <div
                          className={`w-3 h-3 rounded-full ${
                            skill.level === "Intermediate" ||
                            skill.level === "Proficient"
                              ? "bg-emerald-400"
                              : "bg-gray-600"
                          }`}
                        ></div>
                        <div
                          className={`w-3 h-3 rounded-full ${
                            skill.level === "Proficient"
                              ? "bg-emerald-400"
                              : "bg-gray-600"
                          }`}
                        ></div>
                      </div>
                      <span className="ml-2 text-xs text-gray-400">
                        {skill.level}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Coding Profiles Tab */}
          {activeTab === "coding" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="bg-gray-800/25 rounded-xl shadow-lg p-6 border border-emerald-400/25"
            >
              <h3 className="text-3xl font-bold text-gray-100 mb-8 flex items-center cursor-default">
                <FaTrophy className="text-emerald-400 mr-3" />
                Coding Journey
              </h3>

              {/* Modern Stats Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <motion.div
                  className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 rounded-2xl p-6 border border-emerald-400/20"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-emerald-400 text-sm font-medium">
                        Total Problems
                      </p>
                      <p className="text-3xl font-bold text-white">900+</p>
                    </div>
                    <FaCodeBranch className="text-emerald-400 text-2xl" />
                  </div>
                </motion.div>

                <motion.div
                  className="bg-gradient-to-r from-orange-500/10 to-yellow-500/10 rounded-2xl p-6 border border-orange-400/20"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-400 text-sm font-medium">
                        Platforms Active
                      </p>
                      <p className="text-3xl font-bold text-white">6</p>
                    </div>
                    <FaTrophy className="text-orange-400 text-2xl" />
                  </div>
                </motion.div>

                <motion.div
                  className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl p-6 border border-purple-400/20"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-400 text-sm font-medium">
                        Highest Contest Rating
                      </p>
                      <p className="text-3xl font-bold text-white">1591</p>
                    </div>
                    <FaMedal className="text-purple-400 text-2xl" />
                  </div>
                </motion.div>
              </div>

              {/* Platform Cards - Modern Grid Layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {codingAchievements.map((platform, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-800/60 to-gray-900/60 border border-gray-700/50 hover:border-emerald-400/50 transition-all duration-300"
                    whileHover={{ y: -5, scale: 1.02 }}
                  >
                    {/* Background Glow Effect */}
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                      style={{
                        background: `linear-gradient(135deg, ${platform.color}20, transparent)`,
                      }}
                    />

                    {/* Platform Header */}
                    <div className="p-6 border-b border-gray-700/30">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: `${platform.color}20` }}
                          >
                            <platform.icon
                              size={24}
                              style={{ color: platform.color }}
                            />
                          </div>
                          <div>
                            <h4 className="text-lg font-bold text-white group-hover:text-emerald-400 transition-colors">
                              {platform.platform}
                            </h4>
                            <p className="text-gray-400 text-xs">
                              View Profile
                            </p>
                          </div>
                        </div>
                        <a
                          href={platform.profileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 hover:bg-emerald-500/30 transition-colors hover:scale-110 hover:rotate-12 transform duration-200"
                        >
                          <FaExternalLinkAlt size={14} />
                        </a>
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="p-6">
                      <div className="grid grid-cols-1 gap-4">
                        {platform.stats.map((stat, statIndex) => (
                          <motion.div
                            key={statIndex}
                            className="flex items-center justify-between p-3 rounded-xl bg-gray-900/30 group-hover:bg-gray-900/50 transition-colors"
                            whileHover={{ x: 5 }}
                          >
                            <div className="flex items-center space-x-3">
                              <div
                                className="w-8 h-8 rounded-lg flex items-center justify-center"
                                style={{
                                  backgroundColor: `${platform.color}15`,
                                }}
                              >
                                <stat.icon
                                  size={14}
                                  style={{ color: platform.color }}
                                />
                              </div>
                              <span className="text-gray-300 text-sm font-medium">
                                {stat.label}
                              </span>
                            </div>
                            <span
                              className="text-lg font-bold"
                              style={{ color: platform.color }}
                            >
                              {stat.value}
                            </span>
                          </motion.div>
                        ))}
                      </div>

                      {/* Quick Achievement Badge */}
                      <div className="mt-4 p-3 rounded-xl bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-400/20">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                          <span className="text-emerald-400 text-xs font-medium">
                            {index === 0 && "Top 25% Globally"}
                            {index === 1 && "13 Badges Earned"}
                            {index === 2 && "Institute Ranked"}
                            {index === 3 && "Interview Ready"}
                            {index === 4 && "Certified Developer"}
                            {index === 5 && "Contest Participant"}
                          </span>
                        </div>
                      </div>

                      {/* Profile Link Button */}
                      <a
                        href={platform.profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 w-full flex items-center justify-center py-3 px-4 bg-gradient-to-r from-gray-900/50 to-gray-800/50 hover:from-emerald-500/20 hover:to-emerald-600/20 text-white hover:text-emerald-300 rounded-lg transition-all duration-300 border border-gray-700 hover:border-emerald-400 group/link shadow-lg hover:scale-[1.02] transform"
                      >
                        <span className="font-medium">
                          Visit {platform.platform} Profile
                        </span>
                        <FaExternalLinkAlt className="ml-2 text-sm transition-transform duration-300 group-hover/link:translate-x-1" />
                      </a>
                    </div>

                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                  </motion.div>
                ))}
              </div>

              {/* Codolio CTA - Enhanced Design */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 }}
                className="relative"
              >
                <a
                  href="https://codolio.com/profile/CodeTirtho97/card"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block relative overflow-hidden rounded-2xl border-2 border-emerald-400/30 shadow-xl transform transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl bg-gradient-to-r from-gray-900 via-emerald-900/20 to-gray-900 group hover:border-emerald-400/60"
                >
                  {/* Animated Background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/5 via-cyan-400/5 to-emerald-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  {/* Floating Particles Effect */}
                  <div className="absolute inset-0 overflow-hidden">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute w-2 h-2 bg-emerald-400/20 rounded-full animate-pulse"
                        style={{
                          left: `${20 + i * 30}%`,
                          top: `${30 + i * 15}%`,
                          animationDelay: `${i * 0.5}s`,
                        }}
                      />
                    ))}
                  </div>

                  {/* Content */}
                  <div className="relative p-8 flex items-center justify-between z-10">
                    <div className="flex items-center space-x-6">
                      {/* Icon */}
                      <div className="relative">
                        <div className="w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 rounded-2xl flex items-center justify-center border border-emerald-400/30 group-hover:border-emerald-400/60 transition-all duration-300">
                          <FaCode className="w-8 h-8 text-emerald-400 group-hover:scale-110 transition-transform duration-300" />
                        </div>
                        <div className="absolute -top-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                          <FaRocket className="w-3 h-3 text-white" />
                        </div>
                      </div>

                      {/* Text Content */}
                      <div>
                        <h4 className="text-2xl font-bold text-white group-hover:text-emerald-400 transition-colors duration-300 mb-2">
                          Complete Coding Portfolio
                        </h4>
                        <p className="text-gray-300 group-hover:text-gray-200 transition-colors duration-300 mb-3">
                          Explore my comprehensive coding journey with detailed
                          analytics and achievements
                        </p>
                        <div className="flex items-center space-x-4 text-sm">
                          <div className="flex items-center space-x-1 text-emerald-400">
                            <FaCodeBranch className="w-3 h-3" />
                            <span>900+ Problems</span>
                          </div>
                          <div className="flex items-center space-x-1 text-cyan-400">
                            <FaTrophy className="w-3 h-3" />
                            <span>6 Platforms</span>
                          </div>
                          <div className="flex items-center space-x-1 text-purple-400">
                            <FaFire className="w-3 h-3" />
                            <span>Active Coder</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* CTA Button */}
                    <motion.div
                      whileHover={{ x: 5 }}
                      className="flex items-center space-x-3 bg-emerald-500/10 hover:bg-emerald-500/20 px-6 py-3 rounded-xl border border-emerald-400/30 hover:border-emerald-400/60 transition-all duration-300"
                    >
                      <span className="text-emerald-400 font-semibold">
                        Explore Now
                      </span>
                      <FaExternalLinkAlt className="w-4 h-4 text-emerald-400 group-hover:translate-x-1 transition-transform duration-300" />
                    </motion.div>
                  </div>
                </a>
              </motion.div>
            </motion.div>
          )}

          {/* Publications Tab */}
          {activeTab === "publications" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="bg-gray-800/25 rounded-xl shadow-lg p-6 border border-emerald-400/25"
            >
              {/* Toggle between Dev.to and Medium */}
              <div className="flex justify-center mb-8">
                <div className="bg-gray-800/60 p-1 rounded-lg inline-flex">
                  <button
                    onClick={() => setActivePubTab("devto")}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 flex items-center ${
                      activePubTab === "devto"
                        ? "bg-emerald-400/20 text-emerald-400"
                        : "text-gray-400 hover:text-emerald-400"
                    }`}
                  >
                    <SiDevdotto className="mr-2" />
                    Dev.to Articles
                  </button>
                  <button
                    onClick={() => setActivePubTab("medium")}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 flex items-center ${
                      activePubTab === "medium"
                        ? "bg-emerald-400/20 text-emerald-400"
                        : "text-gray-400 hover:text-emerald-400"
                    }`}
                  >
                    <SiMedium className="mr-2" />
                    Medium Articles
                  </button>
                </div>
              </div>

              {/* Dev.to Articles */}
              {activePubTab === "devto" && (
                <div>
                  <div className="flex justify-between items-center mb-8">
                    <h4 className="text-2xl font-medium text-gray-100 flex items-center">
                      <SiDevdotto className="text-orange-600 mr-2 text-3xl" />
                      Popular Articles
                      <span className="ml-2 text-sm text-gray-400 font-normal">
                        (sorted by engagement)
                      </span>
                    </h4>
                    <a
                      href="https://dev.to/codetirtho97"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 text-sm font-medium text-emerald-500 border border-emerald-500 hover:bg-emerald-500 hover:text-white rounded-lg shadow transition-transform transform hover:scale-105"
                    >
                      See More →
                    </a>
                  </div>

                  {devToArticles.length === 0 ? (
                    <div className="text-center py-10">
                      <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-400"></div>
                      <p className="mt-4 text-gray-400">Loading articles...</p>
                    </div>
                  ) : (
                    <div className="space-y-8">
                      {/* Featured Article (Most Popular) */}
                      {devToArticles.length > 0 && (
                        <motion.a
                          href={devToArticles[0].link}
                          target="_blank"
                          rel="noopener noreferrer"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5 }}
                          className="group block bg-gradient-to-r from-orange-900/20 via-gray-800/40 to-gray-900/40 rounded-2xl overflow-hidden border border-orange-500/20 hover:border-orange-400/40 shadow-xl hover:shadow-orange-400/10 transition-all duration-300"
                        >
                          <div className="flex flex-col lg:flex-row">
                            {/* Image Section */}
                            <div className="lg:w-2/5 h-64 lg:h-auto relative overflow-hidden">
                              <img
                                src={devToArticles[0].cover}
                                alt={devToArticles[0].title}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                onError={(e) => {
                                  e.target.src = "/images/default_cover.jpg";
                                }}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent group-hover:from-black/40 transition-all duration-300"></div>

                              {/* Floating Badge */}
                              <div className="absolute top-4 left-4 flex items-center gap-2">
                                <div className="bg-orange-500/90 text-white text-xs px-3 py-1.5 rounded-full font-bold flex items-center gap-1">
                                  <SiDevdotto className="text-sm" />
                                  FEATURED
                                </div>
                                {devToArticles[0].reactions > 0 && (
                                  <div className="bg-pink-500/90 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1">
                                    <span>❤️</span>
                                    {devToArticles[0].reactions}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Content Section */}
                            <div className="lg:w-3/5 p-8 flex flex-col justify-center">
                              <div className="flex flex-wrap gap-2 mb-4">
                                {devToArticles[0].tags
                                  .slice(0, 3)
                                  .map((tag, idx) => (
                                    <span
                                      key={idx}
                                      className="text-xs px-3 py-1 bg-orange-400/20 text-orange-300 border border-orange-400/30 rounded-full"
                                    >
                                      #{tag}
                                    </span>
                                  ))}
                              </div>

                              <h3 className="text-2xl lg:text-3xl font-bold text-gray-100 group-hover:text-orange-400 transition-colors mb-4 leading-tight">
                                {devToArticles[0].title}
                              </h3>

                              <div className="flex items-center justify-between text-sm text-gray-400 mb-6">
                                <span>{devToArticles[0].date}</span>
                                {devToArticles[0].readingTime > 0 && (
                                  <span className="flex items-center gap-1">
                                    <FaBookReader className="text-xs" />
                                    {devToArticles[0].readingTime} min read
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center text-orange-400 font-medium group-hover:text-orange-300 transition-colors">
                                <span className="mr-2">Read Full Article</span>
                                <FaExternalLinkAlt className="text-sm transition-transform duration-300 group-hover:translate-x-1" />
                              </div>
                            </div>
                          </div>
                        </motion.a>
                      )}

                      {/* Regular Articles Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {devToArticles.slice(1, 7).map((article, index) => (
                          <motion.a
                            key={article.id || index}
                            href={article.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                              duration: 0.5,
                              delay: (index + 1) * 0.1,
                            }}
                            className="group bg-gray-800/40 border border-gray-700/50 hover:border-orange-400/40 rounded-xl overflow-hidden shadow-lg hover:shadow-orange-400/10 transform hover:scale-[1.02] transition-all duration-300 flex flex-col h-full relative"
                          >
                            {/* Popularity Indicator */}
                            {article.reactions > 0 && (
                              <div className="absolute top-3 right-3 z-10 bg-pink-500/90 text-white text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1 shadow-lg">
                                <span>❤️</span>
                                {article.reactions}
                              </div>
                            )}

                            {/* Image Container */}
                            <div className="h-48 overflow-hidden relative">
                              <img
                                src={article.cover}
                                alt={article.title}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                onError={(e) => {
                                  e.target.src = "/images/default_cover.jpg";
                                }}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                              {/* Primary Tag */}
                              {article.tags && article.tags.length > 0 && (
                                <div className="absolute bottom-3 left-3 bg-gray-900/90 text-orange-400 text-xs px-3 py-1.5 rounded-full font-medium border border-orange-400/30">
                                  #{article.tags[0]}
                                </div>
                              )}
                            </div>

                            {/* Content Container */}
                            <div className="p-5 flex flex-col flex-grow">
                              <h4 className="text-lg font-bold text-gray-100 group-hover:text-orange-400 transition-colors line-clamp-2 mb-3 leading-tight">
                                {article.title}
                              </h4>

                              {/* Article Stats */}
                              <div className="flex items-center justify-between text-xs text-gray-400 mb-4">
                                <span>{article.date}</span>
                                {article.readingTime > 0 && (
                                  <span className="flex items-center gap-1">
                                    <FaBookReader />
                                    {article.readingTime}m
                                  </span>
                                )}
                              </div>

                              {/* Bottom Section */}
                              <div className="mt-auto flex items-center justify-between">
                                <div className="flex flex-wrap gap-1">
                                  {article.tags.slice(1, 3).map((tag, idx) => (
                                    <span
                                      key={idx}
                                      className="text-xs px-2 py-0.5 bg-gray-700/50 text-gray-400 rounded"
                                    >
                                      #{tag}
                                    </span>
                                  ))}
                                </div>

                                <span className="text-orange-400 text-sm flex items-center opacity-0 group-hover:opacity-100 transition-all duration-300 font-medium">
                                  Read
                                  <FaExternalLinkAlt className="ml-1.5 text-xs group-hover:translate-x-1 transition-transform duration-300" />
                                </span>
                              </div>
                            </div>
                          </motion.a>
                        ))}
                      </div>

                      {/* Call to Action */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1 }}
                        className="text-center mt-8"
                      >
                        <a
                          href="https://dev.to/codetirtho97"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-600/20 to-orange-500/20 hover:from-orange-600 hover:to-orange-500 text-orange-400 hover:text-white px-6 py-3 rounded-xl border border-orange-500/30 hover:border-orange-500 transition-all duration-300 font-medium shadow-lg hover:shadow-orange-400/20"
                        >
                          <SiDevdotto className="text-lg" />
                          Explore All Articles on Dev.to
                          <FaExternalLinkAlt className="text-sm" />
                        </a>
                      </motion.div>
                    </div>
                  )}
                </div>
              )}

              {/* Medium Articles */}
              {activePubTab === "medium" && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h4 className="text-2xl font-medium text-gray-100 flex items-center">
                      <SiMedium className="text-white mr-2 text-3xl" />
                      Medium Publications
                    </h4>
                    <a
                      href="https://medium.com/@codetirtho97"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 text-sm font-medium text-emerald-500 border border-emerald-500 hover:bg-emerald-500 hover:text-white rounded-lg shadow transition-transform transform hover:scale-105"
                    >
                      Follow Me →
                    </a>
                  </div>

                  {mediumArticles.length === 0 ? (
                    <div className="text-center py-10">
                      <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-400"></div>
                      <p className="mt-4 text-gray-400">Loading articles...</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Featured Article (first article) - Larger display */}
                      {mediumArticles.length > 0 && (
                        <motion.a
                          href={mediumArticles[0].link}
                          target="_blank"
                          rel="noopener noreferrer"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4 }}
                          className="block bg-gradient-to-r from-gray-800/60 to-gray-900/60 rounded-xl p-6 border border-gray-700 hover:border-emerald-400/30 shadow-lg hover:shadow-emerald-400/5 transition-all duration-300 group"
                        >
                          <div className="flex flex-col md:flex-row gap-4 items-center">
                            {/* Article Icon - Styled Medium logo as placeholder for featured article */}
                            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-gray-800 to-gray-700 flex items-center justify-center flex-shrink-0 border-2 border-white/10 group-hover:border-emerald-400/30 transition-all duration-300">
                              <SiMedium className="text-5xl text-white group-hover:text-emerald-400 transition-colors duration-300" />
                            </div>

                            <div className="flex-1 text-center md:text-left">
                              <span className="text-emerald-400 text-sm font-medium mb-2 inline-block px-3 py-1 bg-emerald-400/10 rounded-full">
                                Featured Article
                              </span>
                              <h5 className="text-xl font-bold text-white group-hover:text-emerald-400 transition-colors mb-2">
                                {mediumArticles[0].title}
                              </h5>
                              <p className="text-gray-400 text-sm mb-2">
                                {mediumArticles[0].date}
                              </p>
                              <div className="inline-flex items-center text-emerald-400 text-sm font-medium">
                                Read Article{" "}
                                <FaExternalLinkAlt className="ml-2 text-xs" />
                              </div>
                            </div>
                          </div>
                        </motion.a>
                      )}

                      {/* Article Grid */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {mediumArticles.slice(1, 7).map((article, index) => (
                          <motion.a
                            key={index}
                            href={article.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                            className="bg-gray-800/40 rounded-lg p-4 border border-gray-700 hover:border-emerald-400/30 transition-all duration-300 hover:bg-gray-700/30 flex group"
                          >
                            {/* Article Date with Styled Background */}
                            <div className="flex-shrink-0 w-16 h-16 flex flex-col items-center justify-center bg-gray-900/60 rounded-lg mr-4 border border-gray-700 group-hover:border-emerald-400/20 transition-all duration-300">
                              <span className="text-gray-400 text-xs">
                                {new Date(article.date).toLocaleDateString(
                                  "en-US",
                                  { month: "short" }
                                )}
                              </span>
                              <span className="text-white text-xl font-bold">
                                {new Date(article.date).getDate()}
                              </span>
                            </div>

                            <div className="flex-1">
                              <h5 className="text-white font-medium mb-2 leading-tight line-clamp-2 group-hover:text-emerald-400 transition-colors">
                                {article.title}
                              </h5>
                              <div className="flex justify-between items-center">
                                <span className="text-gray-400 text-xs">
                                  {new Date(article.date).getFullYear()}
                                </span>
                                <span className="text-emerald-400 text-sm flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                                  Read{" "}
                                  <FaExternalLinkAlt className="ml-1 text-xs" />
                                </span>
                              </div>
                            </div>
                          </motion.a>
                        ))}
                      </div>

                      {/* See More Articles Button (if more than 7 articles) */}
                      {mediumArticles.length > 7 && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.6 }}
                          className="mt-4 flex justify-center"
                        >
                          <button
                            onClick={() => {
                              // Either show modal with all articles or navigate to related page
                              window.open(
                                "https://medium.com/@codetirtho97",
                                "_blank"
                              );
                            }}
                            className="px-6 py-3 bg-gradient-to-r from-emerald-500/20 to-emerald-600/20 text-emerald-400 rounded-lg hover:text-white hover:from-emerald-500 hover:to-emerald-600 transition-all duration-300 flex items-center"
                          >
                            See All Articles{" "}
                            <FaExternalLinkAlt className="ml-2" />
                          </button>
                        </motion.div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Experience;
